import type { SequenceRecoveryRecord, TerminalSequenceKind } from '@repo/desktop-bridge';

/**
 * Why this terminal's numbering starts where it does.
 *
 * **A statement, never a control and never a warning.** Nothing on a till can
 * seed a counter and nothing here suggests otherwise: the decision turns on
 * whether the device holds a local counter row, which only the main process can
 * see, and a screen that could act on it is a screen that could reprint a number
 * already in a visitor's hand. So this is one sentence of provenance and there
 * is no button beside it.
 *
 * It exists because the alternative is silence. A terminal that was re-imaged
 * and picked up at 1483 looks, to the person who opens it, exactly like a
 * terminal that has invented a number — and the ordinary response to that is to
 * stop and call somebody. The device knows the honest answer, so it says it.
 *
 * ## What is deliberately *not* drawn
 *
 * **A terminal that legitimately started at 1.** The server had never heard a
 * number from it, so it began at the beginning, which is what anybody expects of
 * a new till. Nothing jumped; drawing a notice would be inventing an event out
 * of the ordinary case, and the empty answer that produced it is a success
 * rather than a gap.
 *
 * **A recovery that is no longer news.** The record itself is permanent — it is
 * in the device's database and it is the audit trail — but a till re-imaged last
 * month is an ordinary till, and a notice that never clears teaches a cashier to
 * stop reading the plate it sits on. So it is stated while somebody might still
 * be asking, and then it is history.
 */

/**
 * How long a recovery stays on screen after it happened.
 *
 * Twenty-four hours rather than "the rest of the day": a terminal re-imaged at
 * ten to midnight would otherwise stop explaining itself an hour later, during
 * the shift that is actually holding the tickets it renumbered.
 */
export const RECOVERY_NOTICE_WINDOW_MS = 24 * 60 * 60 * 1000;

/** One of this terminal's counters, and where it picked up. */
export interface RecoveredCounter {
  readonly kind: TerminalSequenceKind;
  /**
   * The number this counter resumed at — one past the highest the server held.
   *
   * The kinds are reported separately and never summed or merged: `TICKET` and
   * `RECEIPT` number tickets, `SALE` numbers the receipts a sale records and is
   * never issuable as a ticket, and `INVOICE` numbers tax invoices. They are
   * separate counters of one device.
   */
  readonly resumesAt: number;
}

/** What to say about this terminal's numbering, where there is anything to say. */
export interface SequenceRecoveryNotice {
  /** ISO-8601, on the device's effective clock. */
  readonly resolvedAt: string;
  readonly counters: readonly RecoveredCounter[];
}

/**
 * Turn what the device settled into what the counter should say, or `null`.
 *
 * `null` is by far the commonest answer and is not a gap: most tills have always
 * had their own counters, and a screen receiving it draws nothing.
 *
 * Takes `now` rather than reading a clock, per the constitution — and note the
 * window is only ever applied *forwards*. A device whose clock has been wound
 * back behind the recovery still explains itself, because the alternative is
 * that setting a terminal's date wrong silently removes the one sentence that
 * accounts for its numbering.
 */
export function sequenceRecoveryNotice(
  record: SequenceRecoveryRecord | null,
  now: Date,
): SequenceRecoveryNotice | null {
  if (record === null || record.counters.length === 0) {
    return null;
  }

  const since = now.getTime() - Date.parse(record.resolvedAt);

  if (since >= RECOVERY_NOTICE_WINDOW_MS) {
    return null;
  }

  return {
    resolvedAt: record.resolvedAt,
    counters: record.counters.map(({ kind, nextValue }) => ({ kind, resumesAt: nextValue })),
  };
}

/** What a person calls each counter. Not a code, and never the enum on screen. */
export function counterName(kind: TerminalSequenceKind): string {
  switch (kind) {
    case 'TICKET':
      return 'Tickets';
    case 'RECEIPT':
      return 'Ticket receipts';
    case 'SALE':
      return 'Sale receipts';
    case 'INVOICE':
      return 'Tax invoices';
  }
}
